import React from 'react';
import { Helmet } from 'react-helmet-async';

interface Book {
  title: { en: string; fr: string; ar: string };
  description: { en: string; fr: string; ar: string };
  price: string;
  rawPrice: number;
  image: string;
  language: string;
}

interface StructuredDataProps {
  type: 'website' | 'book' | 'author' | 'organization';
  data?: any;
  book?: Book;
  currentLanguage?: string;
}

const StructuredData: React.FC<StructuredDataProps> = ({ 
  type, 
  data, 
  book, 
  currentLanguage = 'fr' 
}) => {
  const baseUrl = 'https://chifaathati.com';
  
  const getWebsiteSchema = () => ({
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Chifaathati",
    "alternateName": "شفاء ذاتي",
    "url": baseUrl,
    "description": "Discover transformative books by Sondess Khalifa in Arabic and French. Personal development, education, and inspiring stories.",
    "inLanguage": ["fr", "ar", "en"],
    "author": {
      "@type": "Person",
      "name": "Sondess Khalifa",
      "jobTitle": "Author & University Professor",
      "url": baseUrl,
      "sameAs": [
        "https://www.youtube.com/@sondesskhalifa7420",
        "https://www.youtube.com/@Sakina-o1p",
        "https://youtube.com/@sondess"
      ]
    },
    "publisher": {
      "@type": "Organization",
      "name": "Chifaathati",
      "url": baseUrl
    },
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": `${baseUrl}/search?q={search_term_string}`
      },
      "query-input": "required name=search_term_string"
    }
  });

  const getBookSchema = (bookData: Book) => {
    const currentTitle = bookData.title[currentLanguage as keyof typeof bookData.title] || bookData.title.en;
    const currentDescription = bookData.description[currentLanguage as keyof typeof bookData.description] || bookData.description.en;
    
    return {
      "@context": "https://schema.org",
      "@type": "Book",
      "name": currentTitle,
      "description": currentDescription,
      "image": `${baseUrl}${bookData.image}`,
      "author": {
        "@type": "Person",
        "name": "Sondess Khalifa",
        "jobTitle": "Author & University Professor"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Chifaathati"
      },
      "offers": {
        "@type": "Offer",
        "price": bookData.rawPrice,
        "priceCurrency": "TND",
        "availability": "https://schema.org/InStock",
        "seller": {
          "@type": "Organization",
          "name": "Chifaathati"
        }
      },
      "inLanguage": bookData.language.toLowerCase().includes('arabic') ? 
        (bookData.language.toLowerCase().includes('french') ? ["ar", "fr"] : ["ar"]) :
        ["fr"],
      "genre": ["Self-help", "Personal Development", "Education"],
      "bookFormat": "https://schema.org/Paperback",
      "numberOfPages": "200", // You can make this dynamic
      "isbn": "", // Add ISBN if available
      "url": `${baseUrl}/books/${encodeURIComponent(currentTitle.toLowerCase().replace(/\s+/g, '-'))}`
    };
  };

  const getAuthorSchema = () => ({
    "@context": "https://schema.org",
    "@type": "Person",
    "name": "Sondess Khalifa",
    "alternateName": "سندس خليفة",
    "jobTitle": "Author & University Professor",
    "description": "University professor and accomplished author with a diverse collection of books in both Arabic and French.",
    "url": baseUrl,
    "image": `${baseUrl}/images/sondess_image.jpg`,
    "sameAs": [
      "https://www.youtube.com/@sondesskhalifa7420",
      "https://www.youtube.com/@Sakina-o1p",
      "https://youtube.com/@sondess"
    ],
    "knowsAbout": [
      "Personal Development",
      "Education",
      "Self-help",
      "Business Coaching",
      "Emotional Intelligence"
    ],
    "worksFor": {
      "@type": "EducationalOrganization",
      "name": "Higher Institute of Commercial Studies of Sousse"
    },
    "nationality": "Tunisian",
    "birthPlace": "Tunisia",
    "alumniOf": {
      "@type": "EducationalOrganization",
      "name": "University"
    }
  });

  const getOrganizationSchema = () => ({
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Chifaathati",
    "alternateName": "شفاء ذاتي",
    "url": baseUrl,
    "logo": `${baseUrl}/images/shifaathati_cover.png`,
    "description": "Publishing house specializing in personal development and educational books in Arabic and French.",
    "founder": {
      "@type": "Person",
      "name": "Sondess Khalifa"
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+216-26-439-062",
      "contactType": "Customer Service",
      "availableLanguage": ["French", "Arabic", "English"]
    },
    "sameAs": [
      "https://www.youtube.com/@sondesskhalifa7420",
      "https://www.youtube.com/@Sakina-o1p"
    ],
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "TN",
      "addressRegion": "Sousse"
    }
  });

  const getBreadcrumbSchema = () => ({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": baseUrl
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Books",
        "item": `${baseUrl}#books`
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": "About",
        "item": `${baseUrl}#about`
      }
    ]
  });

  const getSchema = () => {
    switch (type) {
      case 'website':
        return getWebsiteSchema();
      case 'book':
        return book ? getBookSchema(book) : null;
      case 'author':
        return getAuthorSchema();
      case 'organization':
        return getOrganizationSchema();
      default:
        return data;
    }
  };

  const schema = getSchema();
  
  if (!schema) return null;

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
      {/* Add breadcrumb schema for better navigation */}
      <script type="application/ld+json">
        {JSON.stringify(getBreadcrumbSchema())}
      </script>
    </Helmet>
  );
};

export default StructuredData;
